/**
 * MatchupHeader - Displays the game matchup header with team info
 *
 * Layout (left to right):
 * - Away team (logo + name/Away)
 * - Home team (name/Home + logo)
 * - Total and Spread
 * - Time and Venue
 * - Reset Minutes button
 */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Button, Avatar, useMediaQuery, useTheme } from 'omaha-design-system';
import { ArrowClockwise } from '@phosphor-icons/react';
import { DisplayGame } from '../../data/types';
import { resetGameCustomizations } from '../../store/slice';
import { IRootState } from '../../../../store/reducer';
import { leagueHasInjuryReport, leagueHasEditRates } from '../../data/leagueConfig';

interface MatchupHeaderProps {
  game: DisplayGame;
  onOpenInjuryReport?: () => void;
  onOpenLineups?: () => void;
  /** Header only (no reset / report buttons), e.g. a game whose props haven't opened yet. */
  hideActions?: boolean;
}

const MatchupHeader: React.FC<MatchupHeaderProps> = ({ game, onOpenInjuryReport, onOpenLineups, hideActions = false }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const playerCustomizations = useSelector(
    (state: IRootState) => state.stokasticProps.playerCustomizations
  );
  const filters = useSelector((state: IRootState) => state.stokasticProps.filters);
  const isMLB = filters.league === 'MLB';
  const isNFL = filters.league === 'NFL';
  const showInjuryReport = leagueHasInjuryReport(filters.league);

  // Get player IDs for this game
  const gamePlayerIds = game.players.map((p) => p.id);

  // Check if any players in this game have customizations
  const hasCustomizations = gamePlayerIds.some((id) => playerCustomizations[id]);

  const handleResetMinutes = () => {
    dispatch(resetGameCustomizations(gamePlayerIds));
  };

  // Extract venue from matchup string (e.g., "LAL @ GSW" -> venue would be home team's arena)
  const getVenue = () => {
    // For now just show home team abbreviation as venue placeholder
    return game.homeTeam.name;
  };

  if (isMobile) {
    // Mobile layout - two rows per Figma design
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: 2,
          pb: 2,
          px: 2,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        {/* Row 1: Away Team | Time/Venue | Home Team */}
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
          {/* Away Team - left aligned, flex 1 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            <Avatar
              src={game.awayTeam.logo}
              alt={game.awayTeam.name}
              variant="rounded"
              sx={{ width: 40, height: 40, flexShrink: 0, bgcolor: 'transparent', '& .MuiAvatar-img': { objectFit: 'contain' } }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                color="text.primary"
                sx={{ fontWeight: 600, fontSize: '16px', lineHeight: 1.5 }}
              >
                {game.awayTeam.name}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: '13.33px', lineHeight: 1.5 }}>
                Away
              </Typography>
            </Box>
          </Box>

          {/* Time and Venue - center */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Typography color="text.secondary" sx={{ fontSize: '11.11px', lineHeight: 1 }}>
              {game.time}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: '11.11px', lineHeight: 1 }}>
              {getVenue()}
            </Typography>
          </Box>

          {/* Home Team - right aligned, flex 1 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0, justifyContent: 'flex-end' }}>
            <Box sx={{ textAlign: 'right', minWidth: 0 }}>
              <Typography
                color="text.primary"
                sx={{ fontWeight: 600, fontSize: '16px', lineHeight: 1.5 }}
              >
                {game.homeTeam.name}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: '13.33px', lineHeight: 1.5 }}>
                Home
              </Typography>
            </Box>
            <Avatar
              src={game.homeTeam.logo}
              alt={game.homeTeam.name}
              variant="rounded"
              sx={{ width: 40, height: 40, flexShrink: 0, bgcolor: 'transparent', '& .MuiAvatar-img': { objectFit: 'contain' } }}
            />
          </Box>
        </Box>

        {/* Row 2: Total/Spread | Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Total and Spread */}
          {/* <Box sx={{ display: 'flex', gap: 2 }}>
            <Box>
              <Typography
                color="text.primary"
                sx={{ fontWeight: 600, fontSize: '13.33px', lineHeight: 1.5 }}
              >
                {game.total || '-'}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: '11.11px', lineHeight: 1 }}>
                Total
              </Typography>
            </Box>
            <Box>
              <Typography
                color="text.primary"
                sx={{ fontWeight: 600, fontSize: '13.33px', lineHeight: 1.5 }}
              >
                {game.spread || '-'}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: '11.11px', lineHeight: 1 }}>
                Spread
              </Typography>
            </Box>
          </Box> */}

          {/* Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Reset Button - leagues with editable rates */}
            {!hideActions && leagueHasEditRates(filters.league) && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ArrowClockwise size={14} weight="regular" />}
                onClick={handleResetMinutes}
                disabled={!hasCustomizations}
                sx={{
                  borderColor: 'rgba(242, 242, 243, 0.38)',
                  color: 'text.primary',
                  textTransform: 'none',
                  fontSize: '11.11px',
                  fontWeight: 600,
                  py: 0.75,
                  px: 1,
                  borderRadius: '5.555px',
                  '&:hover': {
                    borderColor: 'rgba(242, 242, 243, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                  '&.Mui-disabled': {
                    borderColor: 'rgba(242, 242, 243, 0.2)',
                    color: 'rgba(242, 242, 243, 0.38)',
                  },
                }}
              >
                {isNFL ? 'Reset Volume & Rates' : 'Reset Mins & Rates'}
              </Button>
            )}

            {/* Injury Report Button - NBA and NFL */}
            {!hideActions && showInjuryReport && onOpenInjuryReport && (
              <Button
                variant="outlined"
                size="small"
                onClick={onOpenInjuryReport}
                sx={{
                  borderColor: 'rgba(242, 242, 243, 0.38)',
                  color: 'text.primary',
                  textTransform: 'none',
                  fontSize: '11.11px',
                  fontWeight: 600,
                  py: 0.75,
                  px: 1,
                  borderRadius: '5.555px',
                  '&:hover': {
                    borderColor: 'rgba(242, 242, 243, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                Injury Report
              </Button>
            )}

            {/* Lineups Button - MLB only */}
            {!hideActions && isMLB && onOpenLineups && (
              <Button
                variant="outlined"
                size="small"
                onClick={onOpenLineups}
                sx={{
                  borderColor: 'rgba(242, 242, 243, 0.38)',
                  color: 'text.primary',
                  textTransform: 'none',
                  fontSize: '11.11px',
                  fontWeight: 600,
                  py: 0.75,
                  px: 1,
                  borderRadius: '5.555px',
                  '&:hover': {
                    borderColor: 'rgba(242, 242, 243, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                Lineups
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // Desktop layout - single row with gap
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        pt: 2,
        pb: 2,
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      {/* Teams Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        {/* Away Team */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={game.awayTeam.logo}
            alt={game.awayTeam.name}
            variant="rounded"
            sx={{ width: 56, height: 56, bgcolor: 'transparent', '& .MuiAvatar-img': { objectFit: 'contain' } }}
          />
          <Box>
            <Typography
              variant="h6"
              color="text.primary"
              sx={{ fontFamily: 'Gilroy', fontWeight: 900, fontSize: '19.2px' }}
            >
              {game.awayTeam.name}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Away
            </Typography>
          </Box>
        </Box>

        {/* Home Team */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant="h6"
              color="text.primary"
              sx={{ fontFamily: 'Gilroy', fontWeight: 900, fontSize: '19.2px' }}
            >
              {game.homeTeam.name}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Home
            </Typography>
          </Box>
          <Avatar
            src={game.homeTeam.logo}
            alt={game.homeTeam.name}
            variant="rounded"
            sx={{ width: 56, height: 56, bgcolor: 'transparent', '& .MuiAvatar-img': { objectFit: 'contain' } }}
          />
        </Box>
      </Box>

      {/* Total and Spread */}
      {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h6"
            color="text.primary"
            sx={{ fontFamily: 'Gilroy', fontWeight: 900, fontSize: '19.2px' }}
          >
            {game.total || '-'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Total
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h6"
            color="text.primary"
            sx={{ fontFamily: 'Gilroy', fontWeight: 900, fontSize: '19.2px' }}
          >
            {game.spread || '-'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Spread
          </Typography>
        </Box>
      </Box> */}

      {/* Time and Venue */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" color="text.secondary" fontSize="13.33px">
          {game.time}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize="13.33px">
          {getVenue()}
        </Typography>
      </Box>

      {/* Buttons */}
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        {/* Reset Minutes & Rates Button - leagues with editable rates */}
        {!hideActions && leagueHasEditRates(filters.league) && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowClockwise size={16} weight="regular" />}
            onClick={handleResetMinutes}
            disabled={!hasCustomizations}
            sx={{
              borderColor: 'rgba(242, 242, 243, 0.38)',
              color: 'text.primary',
              textTransform: 'none',
              fontSize: '13.33px',
              fontWeight: 600,
              py: 1,
              px: 1.5,
              '&:hover': {
                borderColor: 'rgba(242, 242, 243, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
              '&.Mui-disabled': {
                borderColor: 'rgba(242, 242, 243, 0.2)',
                color: 'rgba(242, 242, 243, 0.38)',
              },
            }}
          >
            {isNFL ? 'Reset Volume & Rates' : 'Reset Mins & Rates'}
          </Button>
        )}

        {/* Injury Report Button - NBA and NFL */}
        {!hideActions && showInjuryReport && onOpenInjuryReport && (
          <Button
            variant="outlined"
            size="small"
            onClick={onOpenInjuryReport}
            sx={{
              borderColor: 'rgba(242, 242, 243, 0.38)',
              color: 'text.primary',
              textTransform: 'none',
              fontSize: '13.33px',
              fontWeight: 600,
              py: 1,
              px: 1.5,
              '&:hover': {
                borderColor: 'rgba(242, 242, 243, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            Injury Report
          </Button>
        )}

        {/* Lineups Button - MLB only */}
        {!hideActions && isMLB && onOpenLineups && (
          <Button
            variant="outlined"
            size="small"
            onClick={onOpenLineups}
            sx={{
              borderColor: 'rgba(242, 242, 243, 0.38)',
              color: 'text.primary',
              textTransform: 'none',
              fontSize: '13.33px',
              fontWeight: 600,
              py: 1,
              px: 1.5,
              '&:hover': {
                borderColor: 'rgba(242, 242, 243, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            Lineups
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default MatchupHeader;
